import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, NgZone, ComponentRef, ViewContainerRef } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as Hammer from 'hammerjs';
import { ShopifyService } from '../services/shopify.service';
import { AwsUploadService } from '../services/aws-upload.service';
import { Meta } from '@angular/platform-browser';
import { FirebaseService } from '../services/firebase.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { ImageEditorService, ImageDimensions, FrameBounds, ImageTransformState, TextOverlayState } from '../services/image-editor.service';
import { GestureHandlerService, GestureCallbacks } from '../services/gesture-handler.service';
import { DesignQueueService } from '../services/design-queue.service';
import { CheckoutService } from '../services/checkout.service';
import { PromoService } from '../services/promo.service';
import { MetaPixelService } from '../services/meta-pixel.service';
import { CartService } from '../services/cart.service';
import {
  EditorStateModel,
  ImageTransformStateModel,
  TextOverlayStateModel,
  UIStateModel,
  MagnetDesign,
  SavedImageState
} from './models';
import { GulliStickerComponent } from './gulli-sticker/gulli-sticker.component';
import { ShopifyProductsComponent } from './shopify-products/shopify-products.component';
import { ClientsCreationsComponent } from '../clients-creations/clients-creations.component';
import { ReviewsComponent } from '../reviews/reviews.component';
import { QaComponent } from '../qa/qa.component';
import { PurchaseControlsComponent } from './purchase-controls/purchase-controls.component';
import { FooterComponent } from '../footer/footer.component';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [
        NgClass,
        NgStyle,
        FormsModule,
        TranslateModule,
        GulliStickerComponent,
        ShopifyProductsComponent,
        ClientsCreationsComponent,
        ReviewsComponent,
        QaComponent,
        PurchaseControlsComponent,
        FooterComponent,
    ],
})

export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {

  //lazy load map and lottie
  private mapComponentRef: ComponentRef<any> | null = null;
  private shouldLoadMap = false;
  private lottieComponentRef: ComponentRef<any> | null = null;
  @ViewChild('mapHost', { read: ViewContainerRef }) mapHost!: ViewContainerRef;
  @ViewChild('lottieHost', { read: ViewContainerRef }) lottieHost!: ViewContainerRef;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('imageEditor') imageEditor!: ElementRef;
  @ViewChild('editableImage') editableImage!: ElementRef<HTMLImageElement>;
  @ViewChild('magnetFrame') magnetFrame!: ElementRef;
  @ViewChild('uploadSection') uploadSection!: ElementRef<HTMLElement>;
  // @ViewChild('previewDiv') previewDiv!: ElementRef<HTMLElement>;
  @ViewChild('contentEditableDiv') contentEditableDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('locationSection') locationSectionRef!: ElementRef<HTMLElement>;

  // State models
  editorState = new EditorStateModel();
  imageTransform = new ImageTransformStateModel();
  textOverlay = new TextOverlayStateModel();
  uiState = new UIStateModel();

  private gestureCallbacks: GestureCallbacks = {};

  // Frame boundaries
  frameBounds: FrameBounds = { width: 336, height: 212 };

  private isMobile: boolean = false;
  private readonly TAP_THRESHOLD = 10; // pixels
  private readonly TAP_DURATION = 200; // milliseconds
  base64Image2: string = '';

  // Design queue - exposed as getter for template access
  get designQueue(): MagnetDesign[] {
    return this.designQueueService.getDesigns();
  }

  private savedImageState: SavedImageState | null = null;

  // Simplify the frame constants - remove maxWidth and maxHeight
  private readonly LANDSCAPE_FRAME = {
    width: '80vw',
    height: 'calc(80vw / 1.5833333333)'
  };

  private readonly PORTRAIT_FRAME = {
    width: 'min(80vw, calc(60vh / 1.5833333333))',
    height: 'min(60vh, calc(80vw * 1.5833333333))'
  };
  private isFileInputActive: boolean = false;
  private fileInputTimeout: any = null;
  private lastTouchEnd: number = 0;
  private pendingStateClear: boolean = false;
  private popstateListener!: () => void;
  private handlingPopstate = false;

  selectedLocation: any = null;
  selectedLocationDisplay: string = '';
  price!: any;

  // Dynamic tap hint animation
  currentTapHintIndex: number = 0;
  private tapHintInterval: any = null;
  private popupAutoCloseTimeout: any = null;

  // Video loading state
  isGifLoaded: boolean = false;
  private videoPlayAttempted: boolean = false;

  /** When true (e.g. ref=ig), hero video is hidden and layout is simplified */
  hideHeroVideo: boolean = false;

  // Cart modal state
  showCartModal: boolean = false;

  get showValentinesBanner(): boolean {
    return this.promoService.isValentinesActive();
  }

  get valentinesBundleConfig(): { singlePrice: number; bundlePrice: number } | null {
    return this.promoService.isValentinesActive() ? this.promoService.getValentinesBundleConfig() : null;
  }

  get isMainStore(): boolean {
    return sessionStorage.getItem('main') === 'true';
  }

  get totalCartItems(): number {
    return this.cartService.getTotalQuantity();
  }

  goToAllMagnetsFromFinish(): void {
    this.router.navigate(['/home/magnets'], { state: { fromFinishOrder: true } });
  }

  scrollToCart(): void {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    });
  }

  openCartModal(): void {
    this.showCartModal = true;
  }

  closeCartModal(): void {
    this.showCartModal = false;
    // If we opened the cart from All Magnets, return there when closing
    if (this.route.snapshot.queryParamMap.get('openCart') === '1') {
      this.router.navigate(['/home/magnets'], { replaceUrl: true });
    }
  }

  checkoutFromModal(): void {
    this.closeCartModal();
    this.buyNow();
  }

  get cartModalCurrency(): string {
    return this.price?.price?.currencyCode || 'EUR';
  }

  get cartModalTotal(): number {
    return this.cartService.getTotalPrice(
      this.price?.price?.amount ? parseFloat(this.price.price.amount) : 0
    );
  }

  formatCartPrice(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.cartModalCurrency
    }).format(amount);
  }

  updateCartShopifyQuantity(productId: string, quantity: number): void {
    this.cartService.updateShopifyProductQuantity(productId, quantity);
  }

  removeCartShopifyProduct(productId: string): void {
    this.cartService.removeShopifyProduct(productId);
    if (this.cartService.isEmpty()) {
      this.closeCartModal();
    }
  }

  get cartShopifyProducts(): Array<{ product: any; quantity: number }> {
    return this.cartService.getShopifyProducts();
  }

  scrollToUploadSection(): void {
    if (this.uploadSection?.nativeElement) {
      const offset = 100; // Banner height
      const elementPosition = this.uploadSection.nativeElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Scroll to top of the page. Safe on iOS: sets both body and documentElement
   * scrollTop and uses instant scroll (smooth is unreliable on iOS Safari).
   * Call after a short delay when switching views so the DOM has updated.
   */
  private scrollToTop(): void {
    const top = 0;
    window.scrollTo({ top, behavior: 'smooth' });
    document.body.scrollTop = top;
    document.documentElement.scrollTop = top;
  }
 
  //lazy load map
  async loadMapComponent() {
    const { MapComponent } = await import('./map/map.component');
    this.mapHost.clear();
    this.mapComponentRef = this.mapHost.createComponent(MapComponent);

    // Set input - pass the current location display for this specific design
    this.mapComponentRef.setInput('selectedLocationDisplay', this.selectedLocationDisplay);
    // Subscribe to output via OutputRef
    this.mapComponentRef.instance.placeSelected.subscribe((event: any) => this.onPlaceSelected(event));
  }

  //lazy load lottie
  async loadLottieComponent() {
    if (!this.lottieComponentRef) {
      const { LottieComponent } = await import('./lottie/lottie.component');
      this.lottieHost.clear();
      this.lottieComponentRef = this.lottieHost.createComponent(LottieComponent);
    }
  }

  constructor(
    private ngZone: NgZone,
    private shopifyService: ShopifyService,
    private awsUploadService: AwsUploadService,
    private meta: Meta,
    private firebaseService: FirebaseService,
    private router: Router,
    private route: ActivatedRoute,
    private analytics: Analytics,
    private imageEditorService: ImageEditorService,
    private gestureHandlerService: GestureHandlerService,
    private designQueueService: DesignQueueService,
    private checkoutService: CheckoutService,
    private promoService: PromoService,
    private metaPixelService: MetaPixelService,
    public cartService: CartService) {
    this.meta.updateTag({
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
    });

    // Add global event listeners to prevent double-tap zoom
    document.addEventListener('touchend', (event) => {
      const now = Date.now();
      if (now - this.lastTouchEnd <= 300) {
        event.preventDefault();
      }
      this.lastTouchEnd = now;
    }, { passive: false });
  }

  ngOnInit(): void {
    // refferal code
    // this.route.queryParamMap.subscribe((params: { get: (arg0: string) => any; }) => {
    //   const ref = params.get('ref');
    //   if (ref) {
    //     sessionStorage.setItem('rc', ref);
    //     console.log('Referral code stored:', ref);
    //   } else {
    //     console.log('No referral code found.');
    //   }
    // });
    // Add a style element to disable double-tap zoom globally

    let sessionPrice = sessionStorage.getItem('price');
    if (sessionPrice) {
      this.price = JSON.parse(sessionPrice)
    } 
    else {
      this.router.navigate([''])
    }

    // Restore finish-order state when the component re-creates (e.g. after
    // navigating away and back) but the singleton DesignQueueService still
    // holds completed designs.
    if (!this.designQueueService.isEmpty()) {
      this.editorState.isFinishedEditing = true;
      // Trigger the lazy-loaded map so the location section is usable immediately.
      this.shouldLoadMap = true;
      // Re-hydrate the location fields so the map shows the previously entered
      // location when it lazy-loads (loadMapComponent passes selectedLocationDisplay).
      const lastDesign = this.designQueueService.getLastDesign();
      if (lastDesign?.location) {
        this.selectedLocation = lastDesign.location;
        this.selectedLocationDisplay = lastDesign.location.place || '';
        if (lastDesign.location.city && lastDesign.location.city !== lastDesign.location.place) {
          this.selectedLocationDisplay += `, ${lastDesign.location.city}`;
        }
        if (lastDesign.location.country
            && lastDesign.location.country !== lastDesign.location.city
            && lastDesign.location.country !== lastDesign.location.place) {
          this.selectedLocationDisplay += `, ${lastDesign.location.country}`;
        }
      }
    }

    const style = document.createElement('style');
    style.textContent = `
      * {
        touch-action: pan-x pan-y; /* Allows scrolling but prevents double-tap zoom */
      }
      
      /* For elements that need pinch zoom functionality */
      .magnet-frame {
        touch-action: manipulation; /* Allows pinch zoom but prevents double-tap zoom */
      }
    `;
    document.head.appendChild(style);
    logEvent(this.analytics,'ecommerceLoaded');
    this.metaPixelService.trackViewContent({ content_type: 'product' });

    // Open cart modal when navigating from All Magnets (or elsewhere) with openCart=1
    const openCart = this.route.snapshot.queryParamMap.get('openCart') === '1';
    const checkout = this.route.snapshot.queryParamMap.get('checkout') === '1';
    if (openCart) {
      this.showCartModal = true;
      if (checkout) {
        setTimeout(() => this.buyNow(), 100);
      }
    }

    // When returning from the All Magnets page with a finish-order intent,
    // restore the finish-order view (if there are designs to finish) and
    // ensure the user is brought to the top of the page.
    const finishOrderView = this.route.snapshot.queryParamMap.get('finishOrderView') === '1';
    if (finishOrderView && !this.designQueueService.isEmpty()) {
      this.editorState.isFinishedEditing = true;
      this.shouldLoadMap = true;
      setTimeout(() => this.scrollToTop(), 50);
    }

    // Check ref=ig to hide hero video (e.g. Instagram referral layout)
    this.hideHeroVideo = this.route.snapshot.queryParamMap.get('ref') === 'ig';
    if (this.hideHeroVideo) {
      this.isGifLoaded = true; // Show hero content immediately without waiting for video
    } else {
      // Preload the hero video
      this.preloadHeroVideo();
    }

    // Push a history guard entry so the browser back button can be intercepted
    // while the user is in edit mode or the finish-order view.
    history.pushState({ appPage: 'editor' }, '', window.location.href);

    this.popstateListener = () => {
      this.ngZone.run(() => {
        if (this.handlingPopstate) return;

        if (this.isEditMode && this.designQueue.length === 0) {
          // Pure edit mode with no completed designs — reset cleanly to upload
          this.handlingPopstate = true;
          this.handleModeTransition(false);
          history.pushState({ appPage: 'editor' }, '', window.location.href);
          setTimeout(() => { this.handlingPopstate = false; }, 400);
        } else if (!this.isEditMode && (this.editorState.isFinishedEditing || this.designQueue.length > 0)) {
          // Finish-order view — mirror the edit button: go back to editing the
          // last design without showing the confirmation modal.
          const lastDesign = this.getLastDesign();
          history.pushState({ appPage: 'editor' }, '', window.location.href);
          if (lastDesign) {
            this.editDesign(lastDesign.id);
          }
        } else if (this.isEditMode && this.designQueue.length > 0) {
          // Already in edit mode with other designs in the queue — trap the back
          // button so we don't interrupt the active editing session.
          history.pushState({ appPage: 'editor' }, '', window.location.href);
        }
      });
    };

    window.addEventListener('popstate', this.popstateListener);
  }

  ngAfterViewInit(): void {
    if (this.isEditMode) {
      this.setupHammerGestures();
      this.measureFrameBounds();
    }
    // Ensure video plays if it's already loaded
    if (this.isGifLoaded) {
      setTimeout(() => {
        this.ensureVideoPlays();
      }, 100);
    }
  }

  ngAfterViewChecked() {
    if (this.shouldLoadMap && this.mapHost) {
      // Always clear the map component before loading to ensure fresh state
      if (this.mapComponentRef) {
        this.mapHost.clear();
        this.mapComponentRef = null;
      }
      this.loadMapComponent();
      this.shouldLoadMap = false;
    }
    if (this.textOverlay.isTyping && this.textOverlay.shouldFocusContentEditable && this.contentEditableDiv?.nativeElement) {
      this.contentEditableDiv.nativeElement.innerText = this.textOverlay.overlayText || '';
      this.contentEditableDiv.nativeElement.focus();
      setTimeout(() => {
        this.moveCaretToEnd(this.contentEditableDiv.nativeElement);
      }, 0);
      this.textOverlay.shouldFocusContentEditable = false;
    }
    // Ensure hero video plays when it becomes available (only attempt once)
    if (this.isGifLoaded && !this.videoPlayAttempted && this.heroVideo?.nativeElement) {
      const video = this.heroVideo.nativeElement;
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        this.videoPlayAttempted = true;
        this.ensureVideoPlays();
      }
    }
  }

  triggerFileInput(): void {
    // Prevent multiple rapid calls
    if (this.isFileInputActive) {
      return;
    }

    this.isFileInputActive = true;

    if (this.fileInputTimeout) {
      clearTimeout(this.fileInputTimeout);
    }

    this.fileInput.nativeElement.click();

    this.fileInputTimeout = setTimeout(() => {
      this.isFileInputActive = false;
    }, 500); // 1 second debounce
  }

  onFileSelected(event: Event): void {
    if (this.fileInputTimeout) {
      clearTimeout(this.fileInputTimeout);
    }
    this.isFileInputActive = false;

    const input = event.target as HTMLInputElement;

    // If user cancelled file selection and we were waiting to clear states, just return
    if (this.pendingStateClear && (!input.files || input.files.length === 0)) {
      this.pendingStateClear = false;
      // Reset file input value to allow selecting the same file again
      input.value = '';
      return;
    }

    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!file.type.match('image.*')) {
        alert('Please select an image file (JPEG or PNG)');
        this.pendingStateClear = false;
        return;
      }

      // Show loader immediately
      this.editorState.isLoadingImage = true;

      // Clear states if we were waiting to do so (from addAnotherDesign)
      if (this.pendingStateClear) {
        this.clearDesignStates();
        this.pendingStateClear = false;
      }

      // First check image dimensions before proceeding
      this.getImageDimensions(file).then(async dimensions => {
        this.editorState.isPortraitImage = dimensions.isPortrait;

        // Now proceed with loading the image
        const reader = new FileReader();
        reader.onload = async (e: any) => {
          const imageUrl = e.target.result;
          this.editorState.selectedImage = imageUrl;
          this.editorState.isImageSelected = true;

          try {
            // Preload the image before switching to edit mode
            await this.preloadImageForEditor(imageUrl);
            
            // Switch to edit mode (this will handle the rest of initialization)
            this.switchToEditMode();
          } catch (error) {
            console.error('Error loading image:', error);
            this.editorState.isLoadingImage = false;
            alert('Failed to load image. Please try again.');
          }
        };

        reader.readAsDataURL(file);
      }).catch(error => {
        console.error('Error getting image dimensions:', error);
        this.editorState.isLoadingImage = false;
      });
      logEvent(this.analytics,'ecommerceEditingStarted')
    }
  }

  private getImageDimensions(file: File): Promise<ImageDimensions> {
    return this.imageEditorService.getImageDimensions(file);
  }

  private preloadImageForEditor(imageUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  }

  switchToEditMode(): void {
    this.editorState.switchToEditMode();
    this.resetImageTransform();

    // Use requestAnimationFrame for smoother rendering
    requestAnimationFrame(() => {
      this.measureFrameBounds();
      this.setupHammerGestures();
      document.body.classList.add('editor-active');

      if (this.editableImage?.nativeElement) {
        const img = this.editableImage.nativeElement;

        const completeInitialization = () => {
          this.initialScaleImage();
          
          // Hide loader after a minimum display time for smooth UX
          setTimeout(() => {
            this.editorState.isLoadingImage = false;
            
            // Defer Lottie loading until after image is ready
            requestAnimationFrame(() => {
              this.uiState.showTutorial = true;
              this.loadLottieComponent();
              
              // Hide tutorial after 2 seconds
              setTimeout(() => {
                this.uiState.showTutorial = false;
              }, 2000);
            });
          }, 300);
        };

        if (img.complete && img.naturalWidth) {
          completeInitialization();
        } else {
          img.onload = () => completeInitialization();
        }
      } else {
        // Fallback if image element not found
        this.editorState.isLoadingImage = false;
      }
    });
  }

  switchToUploadMode(): void {
    // Only show confirmation if we're in edit mode and have an image
    if ((this.isEditMode || this.editorState.isFinishedEditing) && this.editorState.selectedImage) {
      this.uiState.openDiscardModal();
      this.mapComponentRef = null;
      return;
    }
    this.performUploadModeSwitch();
  }

  rotateLeft(): void {
    this.imageTransform.rotation = (this.imageTransform.rotation - 90) % 360;
    setTimeout(() => this.constrainPosition(), 50);
  }

  rotateRight(): void {
    this.imageTransform.rotation = (this.imageTransform.rotation + 90) % 360;
    setTimeout(() => this.constrainPosition(), 50);
  }

  resetImageTransform(): void {
    this.imageTransform.resetRotation();
    this.initialScaleImage();
  }

  toggleTextInput(): void {
    if (this.textOverlay.isTyping) {
      document.body.style.overflow = 'hidden';
      this.stopTextEditing();
      return;
    }
    document.body.style.overflow = 'auto';
    this.textOverlay.previousTextPosition = {
      x: this.textOverlay.textPositionX,
      y: this.textOverlay.textPositionY
    };
    this.textOverlay.textPositionX = 50;
    this.textOverlay.textPositionY = 50;
    this.textOverlay.isTyping = true;
    this.textOverlay.preventBlur = true; // Reset preventBlur when entering typing mode
    this.textOverlay.shouldFocusContentEditable = true;
    setTimeout(() => {
      if (this.contentEditableDiv?.nativeElement) {
        this.contentEditableDiv.nativeElement.innerText = this.textOverlay.overlayText || '';
      }
    }, 0);
    if (this.textOverlay.overlayText) {
      this.textOverlay.liveText = this.textOverlay.overlayText;
    }
  }

  onDoneClick() {
    this.textOverlay.preventBlur = false;
    this.textOverlay.shouldFocusContentEditable = false; // Prevent re-focusing after blur
    this.contentEditableDiv?.nativeElement.blur(); // This will trigger blur, but now it's allowed
    this.stopTextEditing();
  }

  stopTextEditing(): void {
    this.textOverlay.isTyping = false;

    // Always update the overlay text, even if empty
    this.textOverlay.overlayText = this.textOverlay.liveText;

    this.textOverlay.textPositionX = this.textOverlay.previousTextPosition.x;
    this.textOverlay.textPositionY = this.textOverlay.previousTextPosition.y;

    this.textOverlay.liveText = '';
    this.textOverlay.shouldFocusContentEditable = false;

    // Re-attach text gestures now that the text overlay element exists in the DOM
    if (this.textOverlay.overlayText) {
      this.refreshTextGestures();
    }
  }

  private refreshTextGestures(): void {
    // Allow Angular's *ngIf to render the text overlay before querying it
    setTimeout(() => {
      this.gestureHandlerService.destroyTextGestures();
      const textOverlays = document.querySelectorAll('.text-overlay');
      this.gestureHandlerService.setupTextGestures(
        textOverlays,
        this.gestureCallbacks,
        () => this.magnetFrame?.nativeElement || null
      );
    }, 50);
  }
  
  selectColor(index: number): void {
    this.textOverlay.selectColor(index);
    setTimeout(() => {
      this.moveCaretToEnd(this.contentEditableDiv.nativeElement);
    }, 0);
  }

  selectFont(index: number): void {
    this.textOverlay.selectFont(index);
    setTimeout(() => {
      this.moveCaretToEnd(this.contentEditableDiv.nativeElement);
    }, 0);
  }

  getTextStyle(): any {
    // During typing the contenteditable must stay upright — rotation only applies to the placed overlay
    const rotation = this.textOverlay.isTyping ? 0 : this.textOverlay.textRotation;
    return {
      'color': this.textOverlay.textStyle.color,
      'font-family': this.textOverlay.textStyle.fontFamily,
      'font-size': `${this.textOverlay.textStyle.fontSize}px`,
      'text-shadow': this.textOverlay.textStyle.color === '#FFFFFF' ? '2px 2px 4px rgba(0, 0, 0, 0.8)' : 'none',
      'font-style': 'bold',
      'transform': `translate(-50%, -50%) rotate(${rotation}deg)`,
      'transition': this.textOverlay.textIsGesturing ? 'none' : null
    };
  }

  startTextDrag(event: MouseEvent | TouchEvent): void {
    if (!this.textOverlay.isTyping && !this.textOverlay.overlayText) {
      return;
    }

    // Multi-touch is handled by HammerJS (pinch/rotate) — don't start a drag
    if (event instanceof TouchEvent && event.touches.length > 1) {
      return;
    }

    this.textOverlay.tapStartTime = Date.now();
    if (event instanceof MouseEvent) {
      this.textOverlay.tapStartX = event.clientX;
      this.textOverlay.tapStartY = event.clientY;
    } else {
      this.textOverlay.tapStartX = event.touches[0].clientX;
      this.textOverlay.tapStartY = event.touches[0].clientY;
    }

    this.textOverlay.isTextDragging = true;

    const textElement = event.target as HTMLElement;
    textElement.classList.add('dragging');

    event.stopPropagation();
    event.preventDefault();
  }

  moveText(event: MouseEvent | TouchEvent): void {
    // Second finger arrived mid-drag — cancel the drag and let HammerJS take over
    if (event instanceof TouchEvent && event.touches.length > 1) {
      this.textOverlay.isTextDragging = false;
      return;
    }

    if (!this.textOverlay.isTextDragging) {
      return;
    }

    const frame = this.magnetFrame?.nativeElement;
    if (!frame) return;

    const frameRect = frame.getBoundingClientRect();

    let clientX: number, clientY: number;

    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    }

    // Calculate the position in percentages with smooth movement
    const newX = ((clientX - frameRect.left) / frameRect.width) * 100;
    const newY = ((clientY - frameRect.top) / frameRect.height) * 100;

    // Apply smooth movement with constraints
    if (this.editorState.isPortraitImage) {
      this.textOverlay.textPositionX = Math.max(6, Math.min(94, newX));
      this.textOverlay.textPositionY = Math.max(2, Math.min(98, newY));
    } else {
      this.textOverlay.textPositionX = Math.max(6, Math.min(94, newX));
      this.textOverlay.textPositionY = Math.max(6, Math.min(94, newY));
    }

    event.preventDefault();
    event.stopPropagation();
  }

  stopTextDrag(event?: MouseEvent | TouchEvent): void {
    if (event?.target instanceof HTMLElement) {
      event.target.classList.remove('dragging');
    }

    // Check if this was a tap
    if (event && this.textOverlay.isTextDragging && !this.textOverlay.isTyping) {
      const endTime = Date.now();
      const timeDiff = endTime - this.textOverlay.tapStartTime;

      let endX: number, endY: number;
      if (event instanceof MouseEvent) {
        endX = event.clientX;
        endY = event.clientY;
      } else if (event instanceof TouchEvent && event.changedTouches.length > 0) {
        endX = event.changedTouches[0].clientX;
        endY = event.changedTouches[0].clientY;
      } else {
        this.textOverlay.isTextDragging = false;
        return;
      }

      const moveX = Math.abs(endX - this.textOverlay.tapStartX);
      const moveY = Math.abs(endY - this.textOverlay.tapStartY);

      // If movement was small enough and quick enough, treat it as a tap
      if (timeDiff < this.TAP_DURATION && moveX < this.TAP_THRESHOLD && moveY < this.TAP_THRESHOLD) {
        this.toggleTextInput();
        // Pre-fill the text input with existing text
        this.textOverlay.liveText = this.textOverlay.overlayText;
      }
    }

    this.textOverlay.isTextDragging = false;
  }

  handleKeyboardInput(event: KeyboardEvent): void {
    if (!this.textOverlay.isTyping) {
      return;
    }

    if (event.key === 'Escape') {
      this.stopTextEditing();
      event.preventDefault();
    }
  }

  onBlur() {
    if (this.textOverlay.preventBlur) {
      setTimeout(() => {
        this.contentEditableDiv?.nativeElement.focus();
      }, 0);
    }
  }

  onContentEditableInput(event: Event): void {
    const div = event.target as HTMLElement;
    this.textOverlay.liveText = div.innerText;
  }

  finishEditing(): void {
    this.textOverlay.preventBlur = false;
    this.editorState.isProcessingEdit = true;

    // Save the current state before finishing
    this.savedImageState = {
      rotation: this.imageTransform.rotation,
      positionX: this.imageTransform.positionX,
      positionY: this.imageTransform.positionY,
      maxWidth: this.imageTransform.maxWidth,
      maxHeight: this.imageTransform.maxHeight,
      overlayText: this.textOverlay.overlayText,
      textStyle: { ...this.textOverlay.textStyle },
      textPositionX: this.textOverlay.textPositionX,
      textPositionY: this.textOverlay.textPositionY,
      textRotation: this.textOverlay.textRotation
    };

    // Ensure text editing is stopped properly
    if (this.textOverlay.isTyping) {
      this.stopTextEditing();
    }

    // Capture the edited image with text
    this.captureEditedImage().then(() => {
      // Create a design object and add to queue
      const design: MagnetDesign = {
        id: this.designQueueService.getCurrentDesignId() || this.designQueueService.generateDesignId(),
        base64Image: this.editorState.base64Image,
        pdfBlob: this.editorState.pdfBlob!,
        quantity: 1,
        savedState: { ...this.savedImageState! },
        location: this.selectedLocation, // Location is associated with this specific design
        thumbnailUrl: this.editorState.base64Image,
        originalImageUrl: this.editorState.selectedImage!, // Save the original image URL
        isPortrait: this.editorState.isPortraitImage
      };

      // Add or update design (service handles the logic)
      this.designQueueService.addOrUpdateDesign(design);

      const checkButton = document.querySelector('.editor-footer .btn-icon:last-child');
      if (checkButton) {
        checkButton.classList.add('success-animation');

        setTimeout(() => {
          this.handleModeTransition(true);
          checkButton.classList.remove('success-animation');
          this.editorState.isProcessingEdit = false;
        }, 800);
      } else {
        this.handleModeTransition(true);
        this.editorState.isProcessingEdit = false;
      }
    }).catch(() => {
      this.editorState.isProcessingEdit = false;
    });
    logEvent(this.analytics,'ecommerceImageEditingFinished');
    // Clear map component so it reloads fresh for the buy scene
    this.mapComponentRef = null;
    this.shouldLoadMap = true;
  }

  private setupHammerGestures(): void {
    if (!this.magnetFrame?.nativeElement) return;

    const frameElement = this.magnetFrame.nativeElement;

    // Define callbacks for gesture handling
    const callbacks: GestureCallbacks = this.gestureCallbacks = {
      onPanStart: (centerX: number, centerY: number) => {
        if (!this.textOverlay.isTextDragging && !this.textOverlay.isTyping) {
          this.imageTransform.isDragging = true;
          this.imageTransform.lastTouchX = centerX;
          this.imageTransform.lastTouchY = centerY;
        }
      },
      onPanMove: (deltaX: number, deltaY: number) => {
        if (this.imageTransform.isDragging && !this.textOverlay.isTextDragging) {
          this.imageTransform.positionX += deltaX;
          this.imageTransform.positionY += deltaY;
          this.constrainPosition();
        }
      },
      onPanEnd: () => {
        this.imageTransform.isDragging = false;
      },
      onPinchStart: (scale: number) => {
        if (!this.textOverlay.isTextDragging && !this.textOverlay.isTyping) {
          this.imageTransform.isPinching = true;
          this.imageTransform.initialMaxWidth = this.imageTransform.maxWidth;
          this.imageTransform.initialMaxHeight = this.imageTransform.maxHeight;
          this.imageTransform.initialMaxWidthForGesture = this.imageTransform.maxWidth;
          this.imageTransform.initialMaxHeightForGesture = this.imageTransform.maxHeight;
          this.imageTransform.lastPinchScale = scale;
          this.imageTransform.cumulativeScale = 1.0;
        }
      },
      onPinchMove: (scaleFactor: number, minWidth: number, minHeight: number, maxWidth: number, maxHeight: number) => {
        if (!this.textOverlay.isTextDragging) {
          // Track cumulative scale from gesture start to prevent accumulation errors
          // scaleFactor is relative to the last move, so we accumulate it
          this.imageTransform.cumulativeScale = (this.imageTransform.cumulativeScale || 1.0) * scaleFactor;
          // Use initial dimensions with cumulative scale (absolute from gesture start)
          const newMaxWidth = this.imageTransform.initialMaxWidthForGesture * this.imageTransform.cumulativeScale;
          const newMaxHeight = this.imageTransform.initialMaxHeightForGesture * this.imageTransform.cumulativeScale;

          this.applyDimensionConstraints(newMaxWidth, newMaxHeight, minWidth, minHeight, maxWidth, maxHeight);

          if (this.editableImage?.nativeElement) {
            this.editableImage.nativeElement.style.maxWidth = `${this.imageTransform.maxWidth}px`;
            this.editableImage.nativeElement.style.maxHeight = `${this.imageTransform.maxHeight}px`;
          }

          this.constrainPosition();
        }
      },
      onPinchEnd: () => {
        this.imageTransform.isPinching = false;
        this.imageTransform.lastPinchScale = 1;
        this.imageTransform.cumulativeScale = 1.0;
      },
      onDoubleTap: () => {
        this.resetImageTransform();
      },
      onTextDragStart: () => {
        this.textOverlay.isTextDragging = true;
      },
      onTextDragMove: (centerX: number, centerY: number, frameRect: DOMRect) => {
        if (this.textOverlay.isTextDragging && !this.textOverlay.textIsGesturing) {
          this.textOverlay.textPositionX = ((centerX - frameRect.left) / frameRect.width) * 100;
          this.textOverlay.textPositionY = ((centerY - frameRect.top) / frameRect.height) * 100;

          this.textOverlay.textPositionX = Math.max(10, Math.min(90, this.textOverlay.textPositionX));
          this.textOverlay.textPositionY = Math.max(10, Math.min(90, this.textOverlay.textPositionY));
        }
      },
      onTextDragEnd: () => {
        this.textOverlay.isTextDragging = false;
      },
      onTextGestureStart: () => {
        this.textOverlay.textIsGesturing = true;
      },
      onTextGestureEnd: () => {
        this.textOverlay.textIsGesturing = false;
      },
      onTextPinchMove: (scaleDelta: number) => {
        this.textOverlay.updateFontSize(this.textOverlay.textStyle.fontSize * scaleDelta);
      },
      onTextRotateMove: (rotationDelta: number) => {
        this.textOverlay.textRotation += rotationDelta;
      },
      onNativePinchStart: () => {
        this.imageTransform.isPinching = true;
        this.imageTransform.initialMaxWidth = this.imageTransform.maxWidth;
        this.imageTransform.initialMaxHeight = this.imageTransform.maxHeight;
        this.imageTransform.initialMaxWidthForGesture = this.imageTransform.maxWidth;
        this.imageTransform.initialMaxHeightForGesture = this.imageTransform.maxHeight;
        this.imageTransform.cumulativeScale = 1.0;
      },
      onNativePinchMove: (scale: number, minWidth: number, minHeight: number, maxWidth: number, maxHeight: number) => {
        if (!this.textOverlay.isTextDragging) {
          const newMaxWidth = this.imageTransform.initialMaxWidthForGesture * scale;
          const newMaxHeight = this.imageTransform.initialMaxHeightForGesture * scale;

          this.applyDimensionConstraints(newMaxWidth, newMaxHeight, minWidth, minHeight, maxWidth, maxHeight);

          if (this.editableImage?.nativeElement) {
            this.editableImage.nativeElement.style.maxWidth = `${this.imageTransform.maxWidth}px`;
            this.editableImage.nativeElement.style.maxHeight = `${this.imageTransform.maxHeight}px`;
          }

          this.constrainPosition();
        }
      },
      onNativePinchEnd: () => {
        this.imageTransform.isPinching = false;
      }
    };

    // Setup image gestures
    this.gestureHandlerService.setupImageGestures(
      frameElement,
      callbacks,
      () => this.textOverlay.isTextDragging,
      () => this.textOverlay.isTyping,
      () => this.frameBounds
    );

    // Text overlay gestures are set up lazily in refreshTextGestures()
    // after stopTextEditing() renders the *ngIf element

      // Add native pinch-zoom for iOS Safari
    if (this.isMobile) {
      this.gestureHandlerService.setupNativePinchZoom(
        frameElement,
        callbacks,
        () => this.textOverlay.isTextDragging,
        () => ({ width: this.imageTransform.initialMaxWidthForGesture, height: this.imageTransform.initialMaxHeightForGesture }),
        () => this.frameBounds
      );
    }

  }


  private applyDimensionConstraints(
    newWidth: number, newHeight: number,
    minWidth: number, minHeight: number,
    maxWidth: number, maxHeight: number
  ): void {
    const result = this.imageEditorService.applyDimensionConstraints(
      newWidth,
      newHeight,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      this.imageTransform.originalAspectRatio
    );
    this.imageTransform.maxWidth = result.width;
    this.imageTransform.maxHeight = result.height;
  }


  private measureFrameBounds(): void {
    if (this.magnetFrame?.nativeElement) {
      const frame = this.magnetFrame.nativeElement;
      const rect = frame.getBoundingClientRect();

      this.frameBounds = {
        width: rect.width,
        height: rect.height
      };
    }
  }

  private initialScaleImage(): void {
    if (!this.editableImage?.nativeElement) return;

    const img = this.editableImage.nativeElement;

    if (!img.naturalWidth) {
      img.onload = () => this.initialScaleImage();
      return;
    }

    // Set initial load flag to disable transitions during scaling
    this.imageTransform.isInitialLoad = true;

    // Store the original aspect ratio of the image
    this.imageTransform.originalAspectRatio = img.naturalWidth / img.naturalHeight;

    // Reset position to center
    this.imageTransform.resetPosition();

    // Calculate initial dimensions using the service
    const scaleResult = this.imageEditorService.calculateInitialScale(
      img.naturalWidth,
      img.naturalHeight,
      this.frameBounds,
      this.editorState.isPortraitImage,
      this.imageTransform.displayMode
    );

    this.imageTransform.maxWidth = scaleResult.maxWidth;
    this.imageTransform.maxHeight = scaleResult.maxHeight;
    this.imageTransform.originalAspectRatio = scaleResult.aspectRatio;

    // Store initial values
    this.imageTransform.initialMaxWidth = this.imageTransform.maxWidth;
    this.imageTransform.initialMaxHeight = this.imageTransform.maxHeight;
    this.imageTransform.initialMaxWidthForGesture = this.imageTransform.maxWidth;
    this.imageTransform.initialMaxHeightForGesture = this.imageTransform.maxHeight;

    // Apply the max dimensions to both the editable image and background image
    const maxWidthStyle = `${this.imageTransform.maxWidth}px`;
    const maxHeightStyle = `${this.imageTransform.maxHeight}px`;

    // Disable transitions synchronously before setting dimensions
    img.style.transition = 'none';
    img.style.maxWidth = maxWidthStyle;
    img.style.maxHeight = maxHeightStyle;

    // Also apply to background image if it exists
    const backgroundImg = document.querySelector('.outer-image-container img');
    if (backgroundImg instanceof HTMLElement) {
      backgroundImg.style.transition = 'none';
      backgroundImg.style.maxWidth = maxWidthStyle;
      backgroundImg.style.maxHeight = maxHeightStyle;
    }

    // Apply constraints to ensure the image stays properly positioned
    this.constrainPosition();

    // Smart initial positioning for specific aspect ratios
    this.applySmartInitialPosition();

    // Re-enable transitions after initial load is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Clear inline transition to allow CSS transitions to work again
        img.style.transition = '';
        if (backgroundImg instanceof HTMLElement) {
          backgroundImg.style.transition = '';
        }
        this.imageTransform.isInitialLoad = false;
      });
    });
  }

  private applySmartInitialPosition(): void {
    const img = this.editableImage?.nativeElement;
    if (!img) return;

    // For very tall portrait images in fill mode, position to show the upper portion
    const smartY = this.imageEditorService.calculateSmartInitialPosition(
      this.imageTransform.originalAspectRatio,
      this.imageTransform.displayMode,
      this.imageTransform.maxHeight,
      this.frameBounds,
      img.naturalHeight
    );

    if (smartY !== 0) {
      this.imageTransform.positionY = smartY;
      this.constrainPosition(); // Make sure we don't exceed bounds
    }
  }

  private constrainPosition(): void {
    if (!this.editableImage?.nativeElement) return;

    const img = this.editableImage.nativeElement;

    if (!img.naturalWidth) {
      img.onload = () => this.constrainPosition();
      return;
    }

    const result = this.imageEditorService.constrainPosition(
      this.imageTransform.positionX,
      this.imageTransform.positionY,
      this.imageTransform.maxWidth,
      this.imageTransform.maxHeight,
      this.imageTransform.rotation,
      img.naturalWidth,
      img.naturalHeight,
      this.frameBounds
    );

    this.imageTransform.positionX = result.positionX;
    this.imageTransform.positionY = result.positionY;

    // Apply the same constraints to the background image
    const backgroundImg = document.querySelector('.outer-image-container img');
    if (backgroundImg instanceof HTMLElement) {
      backgroundImg.style.transform = `translate3d(${this.imageTransform.positionX}px, ${this.imageTransform.positionY}px, 0) rotate(${this.imageTransform.rotation}deg)`;
    }
  }

  get isEditMode(): boolean {
    return this.editorState.isEditMode;
  }

  get isUploadMode(): boolean {
    return this.editorState.isUploadMode;
  }

  get imageTransformStyle(): any {
    const gestureActive = this.imageTransform.isDragging || this.imageTransform.isPinching || this.imageTransform.isInitialLoad;
    return {
      'transform': `translate3d(${this.imageTransform.positionX}px, ${this.imageTransform.positionY}px, 0) rotate(${this.imageTransform.rotation}deg)`,
      'transform-origin': 'center center',
      'max-width': `${this.imageTransform.maxWidth}px`,
      'max-height': `${this.imageTransform.maxHeight}px`,
      // Kill ALL transitions while a gesture is active — avoids the "sticky" zoom artifact on large images
      'transition': gestureActive ? 'none' : 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.2s ease, max-height 0.2s ease',
      // Promote to its own GPU compositor layer during any active gesture
      'will-change': gestureActive ? 'transform' : 'auto'
    };
  }

  // Add new method to handle font size changes
  // Add this new method to handle both success and dismiss cases
  private handleModeTransition(isSuccess: boolean = false): void {
    if (this.imageEditor) {
      const editorElement = this.imageEditor.nativeElement;
      editorElement.style.opacity = '0';

      setTimeout(() => {
        this.editorState.switchToUploadMode();
        document.body.classList.remove('editor-active');

        // Only set isFinishedEditing to true for success case
        if (isSuccess) {
          this.editorState.isFinishedEditing = true;
          // Scroll to top after view updates (iOS-safe; delay so DOM has rendered)
          setTimeout(() => this.scrollToTop(), 50);
        } else {
          // Reset states for dismiss case
          this.editorState.isFinishedEditing = false;
          this.editorState.selectedImage = null;
          this.editorState.isImageSelected = false;
          this.textOverlay.overlayText = '';  // Clear overlay text
          this.textOverlay.liveText = '';     // Clear live text
          this.imageTransform.resetRotation();
          this.imageTransform.resetPosition();
        }

        setTimeout(() => {
          if (editorElement) {
            editorElement.style.opacity = '1';
          }
        }, 100);
      }, 300);
    } else {
      this.editorState.switchToUploadMode();
      document.body.classList.remove('editor-active');
      if (isSuccess) {
        this.editorState.isFinishedEditing = true;
        // Scroll to top after view updates (iOS-safe; delay so DOM has rendered)
        setTimeout(() => this.scrollToTop(), 50);
      } else {
        this.editorState.isFinishedEditing = false;
        // Also clear text fields here in case the imageEditor element isn't available
        this.textOverlay.overlayText = '';
        this.textOverlay.liveText = '';
      }
    }
    this.stopTextEditing();
  }

  // Update performUploadModeSwitch to use the new method
  private performUploadModeSwitch(): void {
    this.handleModeTransition(false);
  }

  // Update confirmDiscard to use the new method
  confirmDiscard(): void {
    this.uiState.closeDiscardModal();
    this.handleModeTransition(false);
  }

  // Add this method back
  cancelDiscard(): void {
    this.uiState.closeDiscardModal();
  }


  // Update the captureEditedImage method to properly handle text rendering
  private async captureEditedImage(): Promise<void> {
    if (!this.magnetFrame?.nativeElement) {
      console.error('Required elements not found');
      return;
    }

    try {
      const transformState: ImageTransformState = {
        rotation: this.imageTransform.rotation,
        positionX: this.imageTransform.positionX,
        positionY: this.imageTransform.positionY,
        maxWidth: this.imageTransform.maxWidth,
        maxHeight: this.imageTransform.maxHeight
      };

      const textState: TextOverlayState | null = this.textOverlay.overlayText ? {
        text: this.textOverlay.overlayText,
        positionX: this.textOverlay.textPositionX,
        positionY: this.textOverlay.textPositionY,
        textRotation: this.textOverlay.textRotation,
        style: { ...this.textOverlay.textStyle }
      } : null;

      const result = await this.imageEditorService.captureEditedImage(
        this.magnetFrame.nativeElement,
        this.editorState.selectedImage || '',
        transformState,
        textState,
        this.editorState.isPortraitImage
      );

      this.editorState.base64Image = result.base64Image;
      this.editorState.pdfBlob = result.pdfBlob;
    } catch (error) {
      console.error('Error capturing image:', error);
    }
  }

  returnToEditMode(): void {
    if (this.editorState.isFinishedEditing && this.editorState.selectedImage) {
      // Show loader immediately
      this.editorState.isLoadingImage = true;
      
      // Reset the isFinishedEditing flag
      this.editorState.isFinishedEditing = false;

      // Clear the base64 image to allow for recreating it
      this.editorState.base64Image = '';
      this.mapComponentRef = null;
      this.lottieComponentRef = null;
      
      // Preload the image before switching
      this.preloadImageForEditor(this.editorState.selectedImage).then(() => {
        // Switch to edit mode WITHOUT resetting transforms (we'll restore saved state)
        this.editorState.switchToEditMode();
        document.body.classList.add('editor-active');
        
        // Use requestAnimationFrame for smoother rendering
        requestAnimationFrame(() => {
          // Measure the frame bounds AFTER it has been rendered with correct orientation
          this.measureFrameBounds();
          this.setupHammerGestures();

          // Restore the saved state if available
          if (this.savedImageState && this.editableImage?.nativeElement) {
            const img = this.editableImage.nativeElement;
            
            // Wait for image to load to get natural dimensions
            const restoreState = () => {
              // Calculate and restore the original aspect ratio
              if (img.naturalWidth && img.naturalHeight) {
                this.imageTransform.originalAspectRatio = img.naturalWidth / img.naturalHeight;
              }
              
              // Restore image transformation
              this.imageTransform.rotation = this.savedImageState!.rotation;
              this.imageTransform.positionX = this.savedImageState!.positionX;
              this.imageTransform.positionY = this.savedImageState!.positionY;
              this.imageTransform.maxWidth = this.savedImageState!.maxWidth;
              this.imageTransform.maxHeight = this.savedImageState!.maxHeight;
              this.imageTransform.initialMaxWidth = this.savedImageState!.maxWidth;
              this.imageTransform.initialMaxHeight = this.savedImageState!.maxHeight;
              this.imageTransform.initialMaxWidthForGesture = this.savedImageState!.maxWidth;
              this.imageTransform.initialMaxHeightForGesture = this.savedImageState!.maxHeight;

              // Restore text overlay
              this.textOverlay.overlayText = this.savedImageState!.overlayText;
              this.textOverlay.textStyle = { ...this.savedImageState!.textStyle };
              this.textOverlay.textPositionX = this.savedImageState!.textPositionX;
              this.textOverlay.textPositionY = this.savedImageState!.textPositionY;
              this.textOverlay.textRotation = this.savedImageState!.textRotation ?? 0;

              // Update selected color and font indices based on restored style
              this.updateStyleIndices();

              // Re-attach text gestures if text was restored
              if (this.textOverlay.overlayText) {
                this.refreshTextGestures();
              }

              // Apply the restored state to the DOM elements
              img.style.maxWidth = `${this.imageTransform.maxWidth}px`;
              img.style.maxHeight = `${this.imageTransform.maxHeight}px`;
              
              // Also apply to background image
              const backgroundImg = document.querySelector('.outer-image-container img');
              if (backgroundImg instanceof HTMLElement) {
                backgroundImg.style.maxWidth = `${this.imageTransform.maxWidth}px`;
                backgroundImg.style.maxHeight = `${this.imageTransform.maxHeight}px`;
              }

              // Hide loader after state is restored
              setTimeout(() => {
                this.editorState.isLoadingImage = false;
                
                // Defer Lottie loading until after everything is ready
                requestAnimationFrame(() => {
                  this.uiState.showTutorial = true;
                  this.loadLottieComponent();
                  
                  // Hide tutorial after 2 seconds
                  setTimeout(() => {
                    this.uiState.showTutorial = false;
                  }, 2000);
                });
              }, 300);
            };
            
            // Check if image is already loaded
            if (img.complete && img.naturalWidth) {
              restoreState();
            } else {
              img.onload = () => restoreState();
            }
          } else {
            // Fallback if no saved state
            this.editorState.isLoadingImage = false;
          }
        });
      }).catch(error => {
        console.error('Error loading image for edit mode:', error);
        this.editorState.isLoadingImage = false;
      });
    }
  }

  // Helper method to update the selected indices based on the restored style
  private updateStyleIndices(): void {
    // Update color index
    const colorIndex = this.textOverlay.availableColors.findIndex(color =>
      color.toLowerCase() === this.textOverlay.textStyle.color.toLowerCase());
    if (colorIndex !== -1) {
      this.textOverlay.selectedColorIndex = colorIndex;
    }

    // Update font index
    const fontIndex = this.textOverlay.availableFonts.findIndex(font =>
      font === this.textOverlay.textStyle.fontFamily);
    if (fontIndex !== -1) {
      this.textOverlay.selectedFontIndex = fontIndex;
    }
  }

  buyNow(): void {
    // Prevent multiple simultaneous buy attempts
    if (this.cartService.isEmpty() || this.checkoutService.isProcessing()) {
      return;
    }

    // Validate that all designs have locations (only when custom designs exist)
    if (!this.designQueueService.isEmpty() && !this.allDesignsHaveLocation()) {
      console.warn('Cannot proceed with checkout: some designs are missing locations');
      this.openLocationInfoModal();
      return;
    }

    this.editorState.isProcessingEdit = true;

    const totalQty = this.getTotalQuantity();
    const unitPrice = this.price?.price?.amount != null ? parseFloat(this.price.price.amount) : 0;
    const currency = this.price?.price?.currencyCode || 'EUR';
    const totalValue = totalQty * unitPrice;
    const metaCartInfo = totalValue > 0 && currency
      ? CheckoutService.metaCartInfoFrom(totalValue, currency)
      : undefined;

    // Start checkout process
    this.checkoutService.startCheckout(
      totalQty,
      (state) => {
        this.editorState.isProcessingOrder = state.isProcessingOrder;
        if (!state.isProcessingOrder && !state.buyInProgress) {
          this.editorState.isProcessingEdit = false;
        }
      },
      metaCartInfo
    );

    logEvent(this.analytics,'ecommerceBuyNowTapped');
  }

  get frameStyle() {
    const frame = this.editorState.isPortraitImage ? this.PORTRAIT_FRAME : this.LANDSCAPE_FRAME;
    const outerScale = 1.37;

    return {
      '--frame-width': frame.width,
      '--frame-height': frame.height,
      '--outer-height': `calc(${frame.height} * ${outerScale})`
    };
  }

  onPlaceSelected(location: any) {
    this.selectedLocation = location && location.length ? location[0] : null;

    // Update the display string if location is selected
    if (this.selectedLocation) {
      this.selectedLocationDisplay = this.selectedLocation.place || '';
      if (this.selectedLocation.city && this.selectedLocation.city !== this.selectedLocation.place) {
        this.selectedLocationDisplay += `, ${this.selectedLocation.city}`;
      }
      if (this.selectedLocation.country && this.selectedLocation.country !== this.selectedLocation.city && this.selectedLocation.country !== this.selectedLocation.place) {
        this.selectedLocationDisplay += `, ${this.selectedLocation.country}`;
      }
    } else {
      this.selectedLocationDisplay = '';
    }

    // Update the location for the last design in the queue (the one currently being configured)
    const lastDesign = this.designQueueService.getLastDesign();
    if (lastDesign) {
      console.log('Updating location for design:', lastDesign.id, 'with location:', this.selectedLocation);
      this.designQueueService.updateDesignLocation(lastDesign.id, this.selectedLocation);
    }
  }

  /**
   * Move the caret to the end of a contenteditable element
   */
  private moveCaretToEnd(element: HTMLElement) {
    if (!element || !document.body.contains(element)) return;
    if (document.activeElement !== element) element.focus();
    // If empty, just focus
    if (!element.innerText || element.innerText.length === 0) return;
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false); // Move to end
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  // Design Queue Management Methods - delegate to service
  removeDesign(designId: string): void {
    this.designQueueService.removeDesign(designId);
    
    // Check if the queue is now empty after deletion
    if (this.designQueueService.isEmpty()) {
      // Reset to initial state - show the upload screen
      this.clearDesignStates();
      this.performUploadModeSwitch();
    }
  }

  updateDesignQuantity(designId: string, quantity: number): void {
    this.designQueueService.updateDesignQuantity(designId, quantity);
  }

  onQuantityChange(quantity: number): void {
    // Handle quantity change from bundle selector or quantity controls
    // When hideQuantityControls is false, this represents the total quantity
    // We need to distribute this quantity across designs proportionally
    // For now, if there's only one design, update it; otherwise log a warning
    const designs = this.designQueueService.getDesigns();
    if (designs.length === 1) {
      this.designQueueService.updateDesignQuantity(designs[0].id, quantity);
    } else if (designs.length > 1) {
      // Multiple designs: distribute quantity proportionally
      // This is a simplified approach - you may want more sophisticated logic
      const currentTotal = this.getTotalQuantity();
      if (currentTotal > 0) {
        const ratio = quantity / currentTotal;
        designs.forEach(design => {
          const newQuantity = Math.max(1, Math.round(design.quantity * ratio));
          this.designQueueService.updateDesignQuantity(design.id, newQuantity);
        });
      }
    }
  }

  onUpdateDesignQuantity(event: {id: string, quantity: number}): void {
    this.designQueueService.updateDesignQuantity(event.id, event.quantity);
  }

  editDesign(designId: string): void {
    const design = this.designQueueService.editDesign(designId);
    if (design) {
      // Restore the design for editing - use the ORIGINAL image, not the rendered thumbnail
      this.editorState.selectedImage = design.originalImageUrl;
      this.savedImageState = design.savedState;
      this.selectedLocation = design.location;
      // Update the display string for the location
      if (this.selectedLocation) {
        this.selectedLocationDisplay = this.selectedLocation.place || '';
        if (this.selectedLocation.city && this.selectedLocation.city !== this.selectedLocation.place) {
          this.selectedLocationDisplay += `, ${this.selectedLocation.city}`;
        }
        if (this.selectedLocation.country && this.selectedLocation.country !== this.selectedLocation.city && this.selectedLocation.country !== this.selectedLocation.place) {
          this.selectedLocationDisplay += `, ${this.selectedLocation.country}`;
        }
      } else {
        this.selectedLocationDisplay = '';
      }
      this.editorState.isPortraitImage = design.isPortrait;
      this.editorState.isFinishedEditing = true; // Set this flag so returnToEditMode works
      
      // Clear map component so it reloads with the correct location for this design
      this.mapComponentRef = null;
      
      // Switch to edit mode
      this.returnToEditMode();
    }
  }

  getTotalQuantity(): number {
    return this.designQueueService.getTotalQuantity();
  }

  getTotalPrice(): number {
    return this.designQueueService.getTotalPrice(this.price?.price?.amount ? parseFloat(this.price.price.amount) : 0);
  }

  getLastDesign(): MagnetDesign | null {
    return this.designQueueService.getLastDesign();
  }

  allDesignsHaveLocation(): boolean {
    const designs = this.designQueueService.getDesigns();
    return designs.length > 0 && designs.every(design => design.location !== null && design.location !== undefined);
  }

  /** Open the location modal (user clicked the Info icon or action was blocked). */
  openLocationInfoModal(): void {
    this.uiState.showLocationInfo = true;
  }

  /** Close the location modal and focus the map input (used by "Add location" CTA). */
  closeLocationModalAndFocusMap(): void {
    this.uiState.showLocationInfo = false;
    
    // Scroll to the location section and focus the input
    setTimeout(() => {
      this.locationSectionRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Focus the map input after scroll
      setTimeout(() => {
        // Try to use the component reference first
        if (this.mapComponentRef && this.mapComponentRef.instance && this.mapComponentRef.instance.focusInput) {
          this.mapComponentRef.instance.focusInput();
        } else {
          // Fallback: directly focus the input element by ID
          const input = document.getElementById('autocomplete') as HTMLInputElement;
          if (input) {
            input.focus();
            input.click();
            // Manually trigger the activate search if we can access the component
            if (this.mapComponentRef && this.mapComponentRef.instance) {
              this.mapComponentRef.instance.isActive = true;
            }
          }
        }
      }, 300);
    }, 0);
  }

  toggleDesignList(): void {
    this.uiState.toggleDesignList();
  }

  addAnotherDesign(): void {
    // Validate that all designs have locations before allowing to add another
    if (!this.allDesignsHaveLocation()) {
      this.openLocationInfoModal();
      return;
    }

    this.uiState.showDesignList = false;
    
    // Set flag to clear states only after file is selected
    this.pendingStateClear = true;
    
    // Switch to upload mode first
    this.editorState.switchToUploadMode();
    
    // Automatically trigger file input to open image gallery
    setTimeout(() => {
      this.triggerFileInput();
    }, 100);
  }

  private clearDesignStates(): void {
    this.editorState.isFinishedEditing = false;
    this.editorState.selectedImage = null;
    this.editorState.isImageSelected = false;
    this.textOverlay.overlayText = '';
    this.textOverlay.liveText = '';
    this.imageTransform.resetRotation();
    this.imageTransform.resetPosition();
    this.selectedLocation = null;
    this.selectedLocationDisplay = '';
    this.designQueueService.setCurrentDesignId(null);
    // Clear the map component reference so it reloads for the new design
    this.mapComponentRef = null;
    this.shouldLoadMap = false;
  }

  ngOnDestroy(): void {
    // Clean up previous timers/gestures
    this.gestureHandlerService.destroy();

    if (this.popstateListener) {
      window.removeEventListener('popstate', this.popstateListener);
    }

    if (this.fileInputTimeout) {
      clearTimeout(this.fileInputTimeout);
    }

    if (this.popupAutoCloseTimeout) {
      clearTimeout(this.popupAutoCloseTimeout);
    }

    if (this.tapHintInterval) {
      clearInterval(this.tapHintInterval);
    }
  }

  /**
   * Preload the hero video for better UX
   */
  private preloadHeroVideo(): void {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    
    video.addEventListener('canplaythrough', () => {
      this.isGifLoaded = true;
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        this.ensureVideoPlays();
      });
    });
    
    video.addEventListener('error', () => {
      // If video fails to load, still show the page
      this.isGifLoaded = true;
      console.error('Failed to preload hero video');
    });
    
    video.src = '../../assets/video/gif1.mp4';
    video.load();
  }

  /**
   * Ensure the hero video plays when it's ready
   */
  private ensureVideoPlays(): void {
    if (this.heroVideo?.nativeElement) {
      const video = this.heroVideo.nativeElement;
      // video.playbackRate = 2.0;
      
      // Ensure video is muted and has the right attributes
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;
      
      // Try to play the video
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // Video is playing successfully
        }).catch(err => {
          console.warn('Video autoplay failed:', err);
          // Some browsers require user interaction, so we'll just log the warning
        });
      }
    }
  }

}


